"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { RxCross2 } from "react-icons/rx";
import Button from "../ui/buttons";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";

interface OTPFormProps {
  onClose?: () => void;
  onSuccess?: () => void;
  email?: string;
}

export default function OTPForm({ onClose, onSuccess, email: propEmail }: OTPFormProps) {
  const {
    authEmail,
    verifyOTP,
    resendOTP,
    resendCountdown,
    isLoading,
    error,
    clearError,
  } = usePublicAuth();

  const displayEmail = propEmail || authEmail;
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(6).fill(null));

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, []);

  // Clear error when OTP changes
  useEffect(() => {
    if (localError) setLocalError(null);
    if (error) clearError();
  }, [otp]);

  const handleChange = (value: string, index: number) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next input if a digit was entered and not at the last input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      // Focus on the next empty input or the last one
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setLocalError('Please enter all 6 digits');
      return;
    }

    if (!displayEmail) {
      setLocalError('Email address is missing');
      return;
    }

    setIsVerifying(true);
    setLocalError(null);

    try {
      const result = await verifyOTP(displayEmail, otpCode);

      if (result.success) {
        onSuccess?.();
        onClose?.();
      } else {
        setLocalError(result.message || 'Invalid OTP. Please try again.');
        // Clear OTP on error
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setLocalError('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;

    setLocalError(null);
    setOtp(["", "", "", "", "", ""]);

    const result = await resendOTP();
    if (!result.success) {
      setLocalError(result.message || 'Failed to resend OTP');
    }

    inputRefs.current[0]?.focus();
  };

  const displayError = localError || error;

  // Mask email for display
  const maskedEmail = displayEmail
    ? displayEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : '';

  return (
    <div className="bg-F8F8F8 w-full h-full overflow-x-hidden p-[10px] sm:p-6">
      {/* Header Section */}
      <div className="mb-6">
        <div className="relative pb-4">
          {/* Close Button */}
          <button
            onClick={() => (onClose ? onClose() : window.history.back())}
            className="absolute right-0 top-0 text-808080-600 hover:text-000000 transition-all duration-200 transform hover:scale-110 cursor-pointer"
            aria-label="Close"
          >
            <RxCross2 size={22} />
          </button>
        </div>
      </div>

      {/* Logo */}
      <div className="flex justify-center">
        <Image
          src="https://media.dinamani.com/dinamani/2026-02-27/y9s7ztw3/dinamanilogo.png"
          alt="Dinamani Logo"
          width={100}
          height={43}
          className="object-contain"
        />
      </div>

      {/* Tamil subtitle */}
      <p className="text-center text-4D4D4D text-14-tamil-600 mt-6 mb-6">
        தினமணியுடன் புதுப்பித்த நிலையில் இருங்கள்
      </p>

      {/* Divider */}
      <div className="-mx-[10px] sm:-mx-6 border-b border-DCDCDC mb-6"></div>

      {/* OTP Title */}
      <h2 className="text-center text-18-tamil-700 text-1B1B1B mt-6 mb-3">
        OTP சரிபார்ப்பு
      </h2>

      {/* Description */}
      <p className="text-center text-14-tamil-400 text-1B1B1B px-4 mb-6 leading-[1.5]">
        <span className="text-14-tamil-600">{maskedEmail || displayEmail}</span> என்ற எண்ணிற்கு சரிபார்ப்புக் குறியீட்டை
        அனுப்பியுள்ளோம். உள்நுழைய OTPஐ உள்ளிடவும்
      </p>

      {/* Error Message */}
      {displayError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-14-tamil-400 text-center rounded">
          {displayError}
        </div>
      )}

      {/* OTP Boxes */}
      <div className="flex justify-center flex-wrap gap-1.5 sm:gap-2.5 mb-4 w-full px-1">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={index === 0 ? handlePaste : undefined}
            disabled={isVerifying || isLoading}
            className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 border text-center text-base sm:text-lg md:text-xl text-20-tamil-700 focus:outline-primary focus:ring-1 focus:ring-primary/30 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${displayError ? 'border-red-400' : 'border-DCDCDC'
              }`}
          />
        ))}
      </div>

      {/* Resend OTP */}
      <button
        onClick={handleResend}
        disabled={resendCountdown > 0 || isLoading}
        className={`text-12-tamil-400 text-left ml-1 mb-6 ${resendCountdown > 0 || isLoading
          ? 'text-808080 cursor-not-allowed'
          : 'text-012061 hover:underline cursor-pointer'
          }`}
      >
        {resendCountdown > 0
          ? `Resend OTP (${resendCountdown}s)`
          : 'Resend OTP'}
      </button>

      {/* Verify Button */}
      <Button
        variant="primary"
        onClick={handleVerify}
        disabled={isVerifying || isLoading || otp.join('').length !== 6}
        className="w-full text-18-tamil-700 text-FFFFFF h-[48px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isVerifying ? 'Verifying...' : 'Verify OTP and Proceed'}
      </Button>

      {/* Footer */}
      <div className="text-center mt-auto pt-22 text-4D4D4D text-10-tamil-400">
        <div className="flex justify-center gap-2 mb-2">
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms of Use</a> |
          <a href="/contact-us" target="_blank" rel="noopener noreferrer" className="hover:underline">Contact Us</a>
        </div>

        <p className="leading-[2.1] line-clamp-3">
          ©2021 தினமணி மீடியா பிரைவேட் லிமிடெட். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.
          தனியுரிமைக் கொள்கை மற்றும் பயன்பாட்டு விதிமுறைகள்.
        </p>
      </div>
    </div>
  );
}
